import React, { FC, useMemo } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import {
    BodyTextSmall,
    DeprecatedButtonGroup,
    HeadingText50,
    DeprecatedInlineMessage,
    InputType,
    pxToRem,
} from '@modernhealth/vital-components';

import { useSelector } from 'react-redux';
import { Language } from 'src/modules/languages/models/language';
import { getSortedCountries } from 'src/modules/countries/selectors/countrySelectors';
import {
    renderInputWithFormik,
    renderSelectWithFormik,
} from 'src/modules/dependents/components/FormFields';
import { ScreenContainer } from 'src/modules/dependents/components/ScreenContainer';
import { Trans, useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

export interface EligibleFormValues {
    dateOfBirth: string;
    country: string;
    dependentRelation: string;
}

const formSchema = Yup.object<EligibleFormValues>().shape({
    dateOfBirth: Yup.string().trim().required('form.validation.birthDate.required'),
    country: Yup.string().trim().required('form.validation.country.required'),
    dependentRelation: Yup.string().trim().required('form.validation.relationship.required'),
});

const relationships = {
    spouse: 'Spouse',
    domesticPartner: 'Domestic Partner',
    child: 'Child',
    other_dependent: 'Other Dependent',
};

interface DependentEligibleFormProps {
    onCancel: VoidFunction;
    onSubmit: (formData: EligibleFormValues, buttonCopy: string) => void;
    onContactUs: (buttonCopy: string) => void;
    initialValues: EligibleFormValues;
}

const ContactUs: FC<Pick<DependentEligibleFormProps, 'onContactUs'>> = ({ onContactUs }) => {
    const { t } = useTranslation();
    const text = t('dependents:eligible_form.notice.contact_us');

    return (
        <ContactUsButton onClick={() => onContactUs(text)}>
            <BodyTextSmall underline fontColor="blue55">
                {text}
            </BodyTextSmall>
        </ContactUsButton>
    );
};

export const DependentEligibleForm: FC<DependentEligibleFormProps> = ({
    onCancel,
    onSubmit,
    initialValues,
    onContactUs,
}) => {
    const { t } = useTranslation();
    const sortedCountries = useSelector(getSortedCountries);
    const submitBtnCopy = t('dependents:common.check_eligibility');

    const countryOptions = useMemo(
        () =>
            sortedCountries.map((language: Language) => ({
                label: language.displayName,
                value: language.value,
            })),
        [sortedCountries],
    );

    const relationshipOptions = Object.keys(relationships).map(key => ({
        label: t(`dependents:eligible_form.dependent_relation.${key}`),
        value: relationships[key],
    }));

    return (
        <ScreenContainer>
            <HeadingText50 marginBottom={40} centered css={{ whiteSpace: 'break-spaces' }}>
                {t('dependents:eligible_form.title')}
            </HeadingText50>
            <div data-testid="InviteDependent.EligibleForm">
                <Formik<EligibleFormValues>
                    validationSchema={formSchema}
                    initialValues={initialValues}
                    onSubmit={values => onSubmit(values, submitBtnCopy)}
                    validateOnMount
                >
                    {props => {
                        const renderSelect = renderSelectWithFormik(props, t);
                        const renderInput = renderInputWithFormik(props, t);

                        return (
                            <>
                                {renderSelect('dependentRelation', relationshipOptions)}
                                {renderSelect('country', countryOptions)}
                                {renderInput('dateOfBirth', InputType.DatePicker)}
                                <DeprecatedInlineMessage message="">
                                    <BodyTextSmall>
                                        <Trans
                                            t={t}
                                            i18nKey="dependents:eligible_form.notice"
                                            components={{
                                                link: <ContactUs onContactUs={onContactUs} />,
                                            }}
                                        />
                                    </BodyTextSmall>
                                </DeprecatedInlineMessage>
                                <DeprecatedButtonGroup
                                    firstButtonText={t('dependents:common.back')}
                                    onFirstButtonClick={onCancel}
                                    secondButtonText={submitBtnCopy}
                                    onSecondButtonClick={props.submitForm}
                                    secondButtonDisabled={!props.isValid}
                                    marginTop={30}
                                />
                            </>
                        );
                    }}
                </Formik>
            </div>
        </ScreenContainer>
    );
};

const ContactUsButton = styled.button({
    border: 'none',
    backgroundColor: 'unset',
    padding: 0,
    marginLeft: pxToRem(2),
});
